import {
    FieldMergeFunction,
    gql,
    InMemoryCache,
    InMemoryCacheConfig,
    isReference,
    Reference,
    StoreObject,
} from '@apollo/client';
import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import { memo } from 'memo-deps';
import {
    upgradeAnswerColumnProps,
    upgradeOrInitMultipleVizClientState,
    upgradeOrInitVizClientState,
} from '/@utils/client-state.util';
import { getEmbedQueryParams } from '/@utils/embed-base.util';
import { autoTitleAnswer } from '/@utils/viz/answer-title.util';
import { client } from './apollo-client';
import {
    getCustomChartConfig,
    getResolvedVizsColumnIds,
    getTotalTokens,
    hasUnresolvedTokens,
    mergeVizData,
    parseVizColumnData,
} from './apollo-client.util';
import { validateChartAndDisplayModeForNewQuery } from './services/answer/answer-viz-service';
import {
    ChartConfig,
    ChartViz,
    DisplayMode,
    HeadlineViz,
    LoadingState,
    SagePhrase,
    TableViz,
} from './services/generated/graphql-types';
import { mergeColumnsDataLites } from './services/visualization/table/table-service.util';

const autoTitleAnswerMemo = memo(autoTitleAnswer);
const queryParams = getEmbedQueryParams();
type Viz = ChartViz | HeadlineViz | TableViz;

const embedPreferedDisplayMode = queryParams?.forceTable
    ? DisplayMode.TableMode
    : null;

const logger = create('apollo-client-policies');

/*
 * We should never update the genNo in our cache to a lower value than it currently is. If we ever
 * get a genNo in a response that is older than our cache value, we will ignore that genNo and
 * leave the cache value as-is. See SCAL-96396 for more information.
 */
const mergeGenNo: FieldMergeFunction<boolean, boolean> = (
    existing,
    incoming,
    props,
) => {
    // Allow reverting to older genNo when explicitly requested (e.g., revertPinboardToGenNo)
    if (props?.variables?.allowIncomingGenNo) {
        return incoming;
    }
    if (existing > incoming) {
        return existing;
    }
    return incoming;
};

const mergecontextBookSessions: FieldMergeFunction<string[], string[]> = (
    existing,
    incoming,
) => {
    if (!existing) {
        return incoming;
    }
    const contextBookSessions = [...incoming, ...existing];
    const uniqContextBookSessions = _.uniqBy(
        contextBookSessions,
        'contextBookId',
    );
    return uniqContextBookSessions;
};

const mergeUpdateOnlyPhrases: FieldMergeFunction = (
    existing: Reference,
    incoming: Reference,
    { variables, args, readField, cache },
) => {
    const answer: Reference = readField('answer', incoming);
    // We set this bit on the cache to be comsumed by search-service
    // to set its state properly.
    try {
        cache.writeFragment({
            // eslint-disable-next-line no-underscore-dangle
            id: answer.__ref,
            fragment: gql`
                fragment onlyPhrases on Answer {
                    updateOnlyPhrases @client
                }
            `,
            data: {
                updateOnlyPhrases: variables.updateOnlyPhrases,
            },
        });
    } catch (error) {
        // Suppress "already recomputing" error from Apollo Client
        if (!error?.message?.includes('already recomputing')) {
            throw error;
        }
    }
    // We only get partial viz information in updateTokens for delayed search.
    // As a result, during this merge operation, stale vizProps is used, which leads
    // to inconsistentcies.
    //
    // For example, in SCAL-106262, the old viz props only have two axisConfig values,
    // which causes the chart render to fail upon adding an attribute
    // because three axisConfig values are required now.
    //
    // [TODO]: By design, this should include all of the visualisation data.
    // This architecture is prone to cache issues that are inconsistent.
    if (!variables?.updateOnlyPhrases) {
        upgradeOrInitVizClientState(true)(existing, incoming, {
            readField,
            cache,
        });
    }
    return incoming;
};

const mergeUpdateColumn: FieldMergeFunction = (existing, incoming, props) =>
    props.variables?.updateOnlyPhrases
        ? mergeUpdateOnlyPhrases(existing, incoming, props)
        : upgradeOrInitVizClientState(false)(existing, incoming, props);

const mergeWatchlistKpiAnswer: FieldMergeFunction = (
    existing,
    incoming,
    props,
) => {
    const session = props.readField('answerEditSession', incoming);

    if (isReference(session)) {
        upgradeOrInitVizClientState(false)(undefined, session, props);
    }
    return incoming;
};

// Whenever we get a new sage response, we MUST get Apollo to update certain object data in the
// cache, even if it's the same data as before. There are hooks which only trigger when the
// references to this data changes, and we need those hooks to trigger upon every sage response, or
// else the sage UI will not behave correctly. We use this util to generate a random field for each
// object, so that Apollo thinks the data is different and always updates the cache state.
// [SCAL-121883] [SCAL-140186]
function generateRandomDummyFieldForArrayEntries<T>(
    existing: T[],
    incoming: T[],
): T[] {
    return incoming.map((item: T) => ({
        ...item,
        dummyFieldDoNotUse: Math.random(),
    }));
}

/**
 * keyFields for a session type whose id is a nested BachSessionId
 * (`obj.id.sessionId`) — AnswerEditSession, PinboardEditSession,
 * WorksheetEditSession, FormulaSearchResponse, SageSearchResponse. (ExploreResponse
 * nests one level deeper at `obj.answer.id`; see {@link answerBachIdKeyFields}.)
 *
 * The static array form `['id', ['sessionId']]` throws an Apollo Invariant
 * ("Missing field 'id.sessionId' while extracting keyFields") when `sessionId`
 * is transiently `undefined` — the emptyAnswer / START_AAQ state a mutation
 * writes before a session is established. Under React 18 that unhandled throw,
 * raised inside the mutation-result write/merge path
 * (markMutationResult -> writeToStore -> applyMerges -> identify), tears down
 * the tree and leaves `embed-blitz-container` an empty <div> (CX #1095). The
 * crash fires per surface: AnswerEditSession on the answer/search path,
 * PinboardEditSession on the liveboard path (confirmed by the
 * liveboard-viz CX e2e, which white-screened on PinboardEditSession once
 * AnswerEditSession was fixed), and so on for the rest — so all of them need
 * the guard, not just AnswerEditSession.
 *
 * `obj.id` can arrive in three shapes depending on Apollo's write order:
 * inline (`{ sessionId }`), as a normalized reference
 * (`{ __ref: 'BachSessionId:{"sessionId":"X"}' }`) when the BachSessionId was
 * already stored, or inline-but-sessionless (the emptyAnswer / START_AAQ state
 * the trace shows). Resolve `sessionId` from whichever is present — inline,
 * via `readField` through the ref, or parsed straight out of the ref key (which
 * always encodes it, even before the target lands in cache).
 *
 * When none resolves, return `false` to fall back to Apollo's default identity
 * instead of throwing — that is the fix for the CX #1095 embed crash, where the
 * static array form `['id', ['sessionId']]` threw an Apollo Invariant inside the
 * mutation-result write/merge path
 * (markMutationResult -> writeToStore -> applyMerges -> identify) and the
 * unhandled throw tore down the embed tree, leaving `embed-blitz-container` an
 * empty <div>.
 *
 * When `sessionId` is present, return the exact cache id Apollo's array form
 * produced — `<__typename>:{"id":{"sessionId":"X"}}`. The typename prefix is
 * load-bearing: Apollo prefixes an array-specifier key with `context.typename`,
 * but a keyFields *function* result is used as the id verbatim (Policies.identify
 * in @apollo/client 3.13.5). A bare `{"id":...}` string would silently re-key
 * every existing entry and — since the sibling session types share this helper —
 * collide across types (an AnswerEditSession and a PinboardEditSession with the
 * same sessionId would map to one cache slot). Keep the prefix.
 */
// Apollo does not re-export KeyFieldsContext / ReadFieldFunction from its public
// barrels, so model the slice each keyFields helper actually reads.
type BachReadField = (field: string, from?: StoreObject | Reference) => unknown;
type BachKeyFieldsContext = {
    typename: string | undefined;
    readField?: BachReadField;
};

function resolveBachSessionId(
    bachId: StoreObject | Reference | null | undefined,
    readField?: BachReadField,
): string | null {
    if (bachId == null) {
        return null;
    }
    const inline = bachId as { sessionId?: string; __ref?: string };
    let sessionId = inline.sessionId;
    // Guard the `from` arg: readField(field, undefined) logs Apollo warn #8
    // ("Undefined 'from' passed to readField"). Only follow a real object/ref.
    if (sessionId == null && readField) {
        sessionId = readField('sessionId', bachId) as string | undefined;
    }
    // eslint-disable-next-line no-underscore-dangle
    const ref = inline.__ref;
    if (sessionId == null && typeof ref === 'string') {
        const match = ref.match(/"sessionId":"([^"]+)"/);
        if (match) {
            [, sessionId] = match;
        }
    }
    return sessionId ?? null;
}

const bachIdKeyFields = (
    obj: StoreObject,
    context: BachKeyFieldsContext,
): string | false => {
    // `typename` is typed optional; without it the template literal would mint
    // an "undefined:..." key shared across every session type — fall back to
    // default identity instead of a silent cross-type collision.
    if (!context?.typename) {
        return false;
    }
    const sessionId = resolveBachSessionId(
        obj?.id as StoreObject | Reference | undefined,
        context?.readField,
    );
    return sessionId == null
        ? false
        : `${context.typename}:{"id":{"sessionId":${JSON.stringify(
              sessionId,
          )}}}`;
};

/**
 * Same fix as {@link bachIdKeyFields}, for ExploreResponse — whose
 * BachSessionId lives one level deeper at `obj.answer.id`. The array form was
 * `['answer', ['id', ['sessionId']]]`, so the canonical key is
 * `ExploreResponse:{"answer":{"id":{"sessionId":"X"}}}`.
 */
const answerBachIdKeyFields = (
    obj: StoreObject,
    context: BachKeyFieldsContext,
): string | false => {
    if (!context?.typename) {
        return false;
    }
    const answer = obj?.answer as (StoreObject & Reference) | undefined;
    // `answer` may itself arrive normalized (AnswerEditSession has its own
    // keyFields), i.e. `{ __ref: 'AnswerEditSession:{"id":{"sessionId":"X"}}' }`
    // with no inline `.id` — follow the ref via readField. If the target isn't
    // in cache yet, resolveBachSessionId's ref-key parse on `answer` itself
    // still recovers the sessionId (the AnswerEditSession ref key encodes it).
    let answerId = answer?.id as StoreObject | Reference | undefined;
    if (answerId == null && answer != null && context?.readField) {
        answerId = context.readField('id', answer) as
            | StoreObject
            | Reference
            | undefined;
    }
    const sessionId =
        resolveBachSessionId(answerId, context?.readField) ??
        resolveBachSessionId(answer, context?.readField);
    return sessionId == null
        ? false
        : `${context.typename}:{"answer":{"id":{"sessionId":${JSON.stringify(
              sessionId,
          )}}}}`;
};

/**
 * Same guard for the BachSessionId-shaped types that key directly on their own
 * top-level `sessionId` — BachSessionId, AcSession, BachPinboardSession. The
 * static form `['sessionId']` throws the identical Apollo Invariant on an
 * undefined sessionId, and the liveboard viz-edit flow writes a
 * BachPinboardSession in exactly that transient state (confirmed by the
 * liveboard-viz CX e2e: once the nested *EditSession types were guarded, the
 * crash surfaced one level down on BachPinboardSession). Canonical key is
 * `<__typename>:{"sessionId":"X"}`.
 */
const sessionIdKeyFields = (
    obj: StoreObject,
    context: BachKeyFieldsContext,
): string | false => {
    if (!context?.typename) {
        return false;
    }
    const sessionId = resolveBachSessionId(obj, context?.readField);
    return sessionId == null
        ? false
        : `${context.typename}:{"sessionId":${JSON.stringify(sessionId)}}`;
};

/*
 * Parameter has `keyFields: false`, so each in-scope parameter is stored inline on the Answer
 * and a write replaces the whole list. The spreadsheet route selects only a few parameter
 * fields; without this merge its write drops every other field (owner, defaultValue, ...),
 * leaving the query route's GetInScopeAnswerParameters unreadable from cache, which crashed
 * the data panel on the Spreadsheet -> Query switch [SCAL-339758]. The incoming list stays
 * authoritative for which parameters exist; fields it doesn't carry are kept from the
 * matching cached parameter.
 */
export const mergeInScopeParameters: FieldMergeFunction<StoreObject[]> = (
    existing,
    incoming,
    { readField, mergeObjects },
) => {
    if (!existing?.length || !incoming) {
        return incoming;
    }
    const existingById = new Map(
        existing.map(parameter => [readField('id', parameter), parameter]),
    );
    return incoming.map(parameter => {
        const cachedParameter = existingById.get(readField('id', parameter));
        return cachedParameter
            ? mergeObjects(cachedParameter, parameter)
            : parameter;
    });
};

export const cacheConfig: InMemoryCacheConfig = {
    typePolicies: {
        Answer: {
            fields: {
                isVizPropUpdated(existing) {
                    return existing || false;
                },
                displayMode: {
                    read: (displayMode, { readField }) => {
                        if (displayMode === DisplayMode.Undefined) {
                            return (
                                embedPreferedDisplayMode ||
                                readField('suggestedDisplayMode')
                            );
                        }
                        return displayMode;
                    },
                },
                visualizationsLoading(existing): LoadingState {
                    return existing || LoadingState.Success;
                },
                name: {
                    read: (name, { readField, cache }) => {
                        const displayMode = readField(
                            'displayMode',
                        ) as DisplayMode;
                        const vizs = readField('visualizations') as Array<
                            Reference
                        >;
                        vizs?.forEach(vRef => readField('columns', vRef));
                        const resolvedVizs = vizs
                            ? (vizs.map(
                                  // Accessing a private member here until this is resolved
                                  // https://github.com/apollographql/apollo-client/issues/7459
                                  // eslint-disable-next-line no-underscore-dangle
                                  v => (cache as any).data.data[v.__ref],
                              ) as Array<Viz>)
                            : [];

                        // From setToken mutation we are not getting headline viz objects,
                        // so we are verifying difference in column for the search tokens.
                        const columnIds: string[] = getResolvedVizsColumnIds(
                            displayMode,
                            resolvedVizs,
                        );
                        const customChartConfig = getCustomChartConfig(
                            displayMode,
                            resolvedVizs,
                        );
                        return autoTitleAnswerMemo(
                            [
                                name,
                                displayMode,
                                _.without(resolvedVizs, undefined),
                            ],
                            [vizs, columnIds, name, customChartConfig],
                        );
                    },
                },
                updateOnlyPhrases(existing) {
                    return existing || null;
                },
                inScopeParameters: {
                    merge: mergeInScopeParameters,
                },
            },
        },
        AnswerColumn: {
            keyFields: false,
        },
        AnswerDataResponse: {
            keyFields: false,
        },
        AxisObj: {
            keyFields: false,
        },
        PinboardSourceDetail: {
            keyFields: false,
        },
        PinboardSourceColumn: {
            keyFields: false,
        },
        Container: {
            keyFields: false,
        },
        VizColumn: {
            fields: {
                column: {
                    merge: (existing, incoming) => {
                        return upgradeAnswerColumnProps(incoming);
                    },
                },
            },
        },
        BachSessionId: {
            keyFields: sessionIdKeyFields,
            fields: {
                acSession: {
                    merge: (existing, incoming) => {
                        if (!incoming && existing) {
                            return existing;
                        }
                        return incoming;
                    },
                },
            },
        },
        FormulaSearchResponse: {
            keyFields: bachIdKeyFields,
        },
        AnswerFormula: {
            fields: {
                tokens: {
                    merge: generateRandomDummyFieldForArrayEntries,
                },
            },
        },
        AcSession: {
            keyFields: sessionIdKeyFields,
            fields: {
                genNo: {
                    merge: (existing, incoming, props) => {
                        if (props?.variables?.dontUpdateGenNoInCache) {
                            return existing;
                        }
                        return incoming;
                    },
                },
            },
        },
        AnswerEditSession: {
            keyFields: bachIdKeyFields,
            fields: {
                refAnswer(existing) {
                    return existing || null;
                },
            },
        },
        Parameter: {
            keyFields: false,
        },
        CohortDetails: {
            keyFields: ['cohortColumnId'],
        },
        PinboardEditSession: {
            keyFields: bachIdKeyFields,
            fields: {
                refPinboard(existing) {
                    return existing || null;
                },
            },
        },
        BachPinboardSession: {
            keyFields: sessionIdKeyFields,
            fields: {
                genNo: {
                    merge: mergeGenNo,
                },
                contextBookSessions: {
                    merge: mergecontextBookSessions,
                },
            },
        },
        ContextBook: {
            keyFields: ['contextBookId'],
        },
        MonitorRule: {
            keyFields: ['ruleId'],
        },
        ContainerLayout: {
            keyFields: false,
        },
        Tab: {
            keyFields: false,
        },
        RAnalysisViz: {
            fields: {
                data: { keyArgs: false },
            },
        },
        HeadlineViz: {
            fields: {
                vizProp: {
                    merge: (existing, incoming, { mergeObjects }) => {
                        if (
                            _.isNil(incoming) ||
                            _.isNil(incoming.headlineVizPropVersion)
                        ) {
                            if (_.isNil(existing)) {
                                return incoming;
                            }

                            return existing;
                        }
                        return incoming;
                    },
                },
                data: {
                    keyArgs: false,
                },
            },
        },
        ChartViz: {
            fields: {
                config: {
                    merge: (existing, incoming, { mergeObjects }) => {
                        return mergeObjects(existing, incoming);
                    },
                },
                vizProp: {
                    merge: (existing, incoming) => {
                        /*
                        Case 1: incoming is null, existing is present
                            a. Drilldown on new search
                            b. Any non viz prop operation after new search
                                (local client state is not present in session)
                            c. back button may cause this, if the client state is not preserved in the session
                                (local client sate may not be present in back state)

                            Solution:
                                Generate a new viz Prop and update locally
                                    or
                                Reuse the already existing client state

                        Case 2: incoming is not null and exiting is null
                            a. Drill down on saved answer
                                (new client state is required to be reused, done as part of query as well)

                            Solution:
                                Accept incoming as is, this is always reused from upgrade or init function

                        Case 3: both are null
                            a. First load of new answer
                            b. View load

                            Solution:
                                Accept incoming as is, this is always reused from upgrade or init function

                        Case 4: both are present
                            a. Almost all operations not above.

                            Solution:
                                Accept incoming as is, this is always reused from upgrade or init function
                        */

                        if (_.isNil(incoming) || _.isNil(incoming.version)) {
                            if (_.isNil(existing)) {
                                return incoming;
                            }

                            return existing;
                        }
                        return incoming;
                    },
                },
                data: {
                    keyArgs: false,
                    merge: (existing = [], incoming, props) => {
                        let newIncoming = _.cloneDeep(incoming);
                        if (newIncoming) {
                            newIncoming = newIncoming.map((item: any) => {
                                const newItem = _.cloneDeep(item);
                                newItem.columnDataLite = parseVizColumnData(
                                    newItem.columnDataLite,
                                );
                                return newItem;
                            });
                        }

                        // no need to keep the existing in case of null chartVizParams
                        if (
                            _.get(props, 'args.chartVizParams.config', [])
                                .length === 0
                        ) {
                            return newIncoming;
                        }
                        // we are just appending the incoming data jsons
                        // as we have also appended the axis config for the same
                        // in the chart configuration
                        return [...existing, ...newIncoming];
                    },
                },
            },
        },
        TableViz: {
            fields: {
                vizProp: {
                    merge: (existing, incoming, { mergeObjects }) => {
                        if (
                            _.isNil(incoming) ||
                            _.isNil(incoming.tableVizPropVersion)
                        ) {
                            if (_.isNil(existing)) {
                                return incoming;
                            }
                            return existing;
                        }
                        return incoming;
                    },
                },
                data: {
                    keyArgs: false,
                    merge: (existing, incoming, { args }) => {
                        const newIncoming = _.cloneDeep(incoming);
                        if (newIncoming) {
                            newIncoming.columnDataLite = parseVizColumnData(
                                newIncoming.columnDataLite,
                            );
                        }

                        const offset = _.get(args, 'pagination.offset', 0);
                        const size = _.get(args, 'pagination.size');

                        // if offset is zero or undefined reset the data to incoming
                        if (!offset) {
                            return newIncoming;
                        }

                        const mergedResult = {
                            ...newIncoming,
                            columnDataLite: mergeColumnsDataLites(
                                existing?.columnDataLite,
                                newIncoming?.columnDataLite,
                                offset,
                                size,
                            ),
                        };

                        return mergedResult;
                    },
                },
            },
        },
        WorksheetEditSession: {
            keyFields: bachIdKeyFields,
        },
        WorksheetEditResponse: {
            keyFields: false,
        },
        LessonPlan: {
            keyFields: ['lessonId'],
        },
        SageSearchResponse: {
            keyFields: bachIdKeyFields,
            fields: {
                phrases: {
                    merge: generateRandomDummyFieldForArrayEntries,
                },
                phrasesLoading(existing): LoadingState {
                    return existing || LoadingState.Success;
                },
                isQueryEmpty: {
                    read: (existing, { readField }) => {
                        const phrases: readonly SagePhrase[] = readField(
                            'phrases',
                        );
                        return (
                            !phrases?.length ||
                            phrases.every(p => !p.tokens?.length)
                        );
                    },
                },
                hasUnresolvedTokens: {
                    read: (existing, { readField }) => {
                        const phrases: readonly SagePhrase[] = readField(
                            'phrases',
                        );
                        if (_.isUndefined(phrases)) {
                            return false;
                        }
                        return hasUnresolvedTokens(phrases);
                    },
                },
                totalTokens: {
                    read: (existing, { readField }) => {
                        const phrases: readonly SagePhrase[] = readField(
                            'phrases',
                        );
                        if (_.isUndefined(phrases)) {
                            return 0;
                        }
                        return getTotalTokens(phrases);
                    },
                },
            },
        },
        SageToken: {
            // TODO(Rifdhan): can probably remove after SCAL-92680, since Apollo shouldn't
            // automatically pick up any id field if it is not named "id"
            keyFields: false,
        },
        SageCompletion: {
            fields: {
                tokens: {
                    merge: generateRandomDummyFieldForArrayEntries,
                },
            },
        },
        ExploreResponse: {
            keyFields: answerBachIdKeyFields,
        },
        ActionAssociationDetails: {
            keyFields: ['id', 'vizId'],
        },
        ActionAssociationMapType: {
            keyFields: false,
        },
        CustomActionConfig: {
            keyFields: false,
        },
        Query: {
            fields: {
                ActionsAssociation_getCustomActionsAssociation: {
                    keyArgs: ['id'],
                    merge: (existing, incoming) => {
                        return incoming;
                    },
                },
                Actions_getEmbedActionsAssociation: {
                    keyArgs: false,
                },
                getAnswer: {
                    keyArgs: ['session', ['sessionId']],
                    read(
                        existingDataFromCache: Reference | undefined,
                        { args, toReference, readField },
                    ) {
                        // Don't create reference if sessionId is undefined
                        if (!args?.session?.sessionId) {
                            return existingDataFromCache;
                        }
                        return (
                            existingDataFromCache ||
                            toReference({
                                __typename: 'AnswerEditSession',
                                id: {
                                    sessionId: args.session.sessionId,
                                },
                            })
                        );
                    },
                    merge: upgradeOrInitVizClientState(false),
                },
                getVizData: {
                    merge: mergeVizData(),
                },
                getAnswerDataByQuery: {
                    keyArgs: [
                        'session',
                        ['acSession'],
                        'dataQueries',
                        ['queries'],
                        'vizId',
                    ],
                },
                getPinboard: {
                    keyArgs: ['session', ['sessionId']],
                    read(
                        existingDataFromCache: Reference | undefined,
                        { args, toReference },
                    ) {
                        if (!args?.session?.sessionId) {
                            return existingDataFromCache;
                        }
                        return (
                            existingDataFromCache ||
                            toReference({
                                __typename: 'PinboardEditSession',
                                id: {
                                    sessionId: args.session.sessionId,
                                },
                            })
                        );
                    },
                },
                getPinboardDataSources: {
                    keyArgs: false,
                },
                Worksheet__getLessonPlans: {
                    keyArgs: ['session', ['sessionId']],
                    read(
                        existingDataFromCache: Reference | undefined,
                        { args, toReference },
                    ) {
                        // Don't create reference if sessionId is undefined
                        if (!args?.session?.sessionId) {
                            return existingDataFromCache;
                        }
                        return (
                            existingDataFromCache ||
                            toReference({
                                __typename: 'WorksheetEditSession',
                                id: {
                                    sessionId: args.session.sessionId,
                                },
                            })
                        );
                    },
                },
                getSageSearchResponse: {
                    keyArgs: ['session', ['sessionId']],
                    read(existing, { args, toReference }) {
                        // Don't create reference if sessionId is undefined
                        if (!args?.session?.sessionId) {
                            return existing;
                        }
                        return (
                            existing ||
                            toReference({
                                __typename: 'SageSearchResponse',
                                id: {
                                    sessionId: args.session.sessionId,
                                },
                            })
                        );
                    },
                },
                getSuggestionsForExplore: {
                    keyArgs: ['session', ['sessionId']],
                    read(
                        existingDataFromCache: Reference | undefined,
                        { args, toReference, readField },
                    ) {
                        // Don't create reference if sessionId is undefined
                        if (!args?.session?.sessionId) {
                            return existingDataFromCache;
                        }
                        return (
                            existingDataFromCache ||
                            toReference({
                                __typename: 'ExploreResponse',
                                answer: {
                                    id: {
                                        sessionId: args.session.sessionId,
                                    },
                                },
                            })
                        );
                    },
                },
                getSuggestionsForExploreReplace: {
                    keyArgs: [
                        'session',
                        ['sessionId', 'genNo'],
                        'selectedColumnProperties',
                    ],
                },
                getFormulaSearchResponse: {
                    keyArgs: ['session', ['sessionId']],
                    read(
                        existingDataFromCache: Reference | undefined,
                        { args, toReference },
                    ) {
                        // Don't create reference if sessionId is undefined
                        if (!args?.session?.sessionId) {
                            logger.error(
                                'getFormulaSearchResponse: sessionId is undefined',
                            );
                            return existingDataFromCache;
                        }
                        return (
                            existingDataFromCache ||
                            toReference({
                                __typename: 'FormulaSearchResponse',
                                id: {
                                    sessionId: args.session.sessionId,
                                },
                            })
                        );
                    },
                },
                getSuggestedAnswers: {
                    merge: upgradeOrInitMultipleVizClientState(true),
                },
                getCohortDetails: {
                    keyArgs: ['cohortColumnId'],
                },
                getCohortDependencies: {
                    keyArgs: ['cohortColumnId'],
                },
                getSourcesListForAnswerWithPagination: {
                    keyArgs: [
                        'params',
                        [
                            'subtypes',
                            'pattern',
                            'tagName',
                            'sort',
                            'sortAscending',
                        ],
                    ],

                    merge: (existing, incoming, { args }) => {
                        const mergedSources = existing?.sources
                            ? existing.sources.slice(0)
                            : [];

                        const offset = args?.params?.offset ?? 0;

                        if (incoming.sources) {
                            for (let i = 0; i < incoming.sources.length; ++i) {
                                mergedSources[offset + i] = incoming.sources[i];
                            }
                        }

                        return {
                            ...incoming,
                            sources: mergedSources,
                        };
                    },
                },
            },
        },
        Mutation: {
            fields: {
                Answer__addColumn: {
                    // This is for the Search on Enter case
                    merge: mergeUpdateOnlyPhrases,
                },
                Answer__setTMLString: {
                    // This is for the executeSearch: false from Visual Embed SDK
                    merge: mergeUpdateOnlyPhrases,
                },
                Answer__removeColumn: {
                    // This is for the Search on Enter case
                    merge: mergeUpdateOnlyPhrases,
                },
                Answer__removeColumns: {
                    // This is for the Search on Enter case
                    merge: mergeUpdateOnlyPhrases,
                },
                Answer__addUpdateFilter: {
                    // This is for the Search on Enter case
                    merge: mergeUpdateOnlyPhrases,
                },
                Answer__removeFilter: {
                    // This is for the Search on Enter case
                    merge: mergeUpdateOnlyPhrases,
                },
                Answer__CopyFromId: {
                    merge: upgradeOrInitVizClientState(true),
                },
                Answer__loadSession: {
                    merge: upgradeOrInitVizClientState(true),
                },
                Answer__updateTokens: {
                    keyArgs: ['session', ['sessionId']],
                    merge: (
                        existing: Reference,
                        incoming: Reference,
                        props: any,
                    ) => {
                        const { cache, readField } = props;
                        const handleUpgradeOrInit = upgradeOrInitVizClientState(
                            true,
                        );
                        const newIncoming = handleUpgradeOrInit(
                            existing,
                            incoming,
                            props,
                        );
                        validateChartAndDisplayModeForNewQuery(
                            existing,
                            incoming,
                            props,
                        );
                        return newIncoming;
                    },
                },
                Answer__load: {
                    keyArgs: ['id'],
                    merge: upgradeOrInitVizClientState(false),
                },
                Answer__createFormulaEditSession: {
                    keyArgs: ['acSession', ['sessionId']],
                },
                Answer__sageFormulaSearch: {
                    keyArgs: ['acSession', ['sessionId']],
                },
                Answer__saveFormula: {
                    merge: upgradeOrInitVizClientState(false),
                },
                Answer__setHeadlineColumns: {
                    merge: upgradeOrInitVizClientState(false),
                },
                Answer__updateProperties: {
                    merge: upgradeOrInitVizClientState(false),
                },
                getAnswerFromTokens: {
                    merge: upgradeOrInitVizClientState(false),
                },
                Answer__searchSage: {
                    keyArgs: ['acSession', ['sessionId']],
                },
                Answer__drillDown: {
                    keyArgs: ['session', ['sessionId']],
                    merge: upgradeOrInitVizClientState(true),
                },
                Answer__updateAxisConfig: {
                    keyArgs: ['session', ['sessionId']],
                    merge: upgradeOrInitVizClientState(true),
                },
                Answer__updateChartConfig: {
                    keyArgs: ['session', ['sessionId']],
                    merge: upgradeOrInitVizClientState(true),
                },
                Pinboard__loadContextBook: {
                    merge: upgradeOrInitVizClientState(false, true),
                },
                Watchlist__loadAssociatedKpi: {
                    merge: mergeWatchlistKpiAnswer,
                },
                Answer__updateColumn: {
                    keyArgs: ['session', ['sessionId']],
                    merge: mergeUpdateColumn,
                },
            },
        },
        eureka_FacetValue: {
            keyFields: ['id', 'resultCount'],
        },
    },
};

let TYPE_POLICIES_LOADED = false;
export const apolloCachePolicyLoader = async () => {
    if (!TYPE_POLICIES_LOADED) {
        const apolloCache = client.cache as InMemoryCache;
        apolloCache.policies.addTypePolicies(cacheConfig.typePolicies);
        await new Promise(resolve => setTimeout(resolve, 0));
        TYPE_POLICIES_LOADED = true;
    }
};

export const apolloCachePolicyLoaderSync = () => {
    if (!TYPE_POLICIES_LOADED) {
        const apolloCache = client.cache as InMemoryCache;
        apolloCache.policies.addTypePolicies(cacheConfig.typePolicies);
        TYPE_POLICIES_LOADED = true;
    }
};
